import { NAV_ITEMS, SCREEN_IDS, type ScreenId } from '@/config/navigation';

/**
 * Which nav entry a URL belongs to.
 *
 * Longest-prefix rather than exact match, so a detail route added later
 * (`/users/u_1f2c`) keeps the Users item highlighted and the breadcrumb rooted
 * at Users, without every new route having to register itself here.
 */
export function screenIdForPath(pathname: string): ScreenId | null {
  const path = normalisePath(pathname);

  if (path === '/') {
    return 'overview';
  }

  let match: ScreenId | null = null;
  let matchLength = 0;

  for (const id of SCREEN_IDS) {
    const candidate = NAV_ITEMS[id].path;
    // The root path is a prefix of everything; it only matches exactly, above.
    if (candidate === '/') continue;

    const isMatch = path === candidate || path.startsWith(`${candidate}/`);
    if (isMatch && candidate.length > matchLength) {
      match = id;
      matchLength = candidate.length;
    }
  }

  return match;
}

function normalisePath(pathname: string): string {
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const withoutQuery = withLeadingSlash.split(/[?#]/)[0] ?? '/';
  const trimmed = withoutQuery.replace(/\/+$/, '');

  return trimmed === '' ? '/' : trimmed;
}

export interface Crumb {
  /** The nav section this URL belongs to. */
  readonly title: string;
  /** Where the back button goes, and what it is called. Absent at a section root. */
  readonly back?: { readonly label: string; readonly path: string };
  /** A sentence beside the title, for a screen whose scope is not obvious. */
  readonly hint?: string;
}

/**
 * Hints that are true of the whole screen rather than of one request.
 *
 * Anything that depends on a filter or a window stays on the screen, where the
 * state that decides it lives; this is only for the standing caveats an
 * operator needs before reading a number.
 */
const HINTS: Partial<Record<ScreenId, string>> = {
  gyms: 'Every gym we set up, and what the gym apps hold, whether or not Steado for Gyms is switched on',
  aispend: 'Who the model bill belongs to',
  broadcasts: 'Sent to every active user',
  notifications: 'Nothing here shows what a notification said',
  activity: 'The record, not a feed: written in the same transaction as the change',
  catalogchanges: 'Food and exercise data changes. Admin activity lists sign-ins and settings.',
  foodrequests: 'What people logged that the catalog did not know, or knew by another name',
};

/**
 * The topbar's heading, plus the way back out of a sub-route.
 *
 * Owning the back link here rather than on each detail screen means "up" is in
 * the same place on every screen, and a new detail route inherits it from its
 * path rather than remembering to draw its own.
 */
export function crumbForPath(pathname: string): Crumb | null {
  const id = screenIdForPath(pathname);
  if (id === null) return null;

  const section = NAV_ITEMS[id];
  const path = normalisePath(pathname);
  const hint = HINTS[id];
  const isSubRoute = path !== section.path;

  return {
    title: section.label,
    ...(isSubRoute ? { back: { label: section.label, path: section.path } } : {}),
    ...(hint === undefined ? {} : { hint }),
  };
}
